import { useCallback, useMemo, useState } from 'react';
import useLocalStorage from '~/hooks/useLocalStorage';

export default function useViewColumns({ storageKey, columns }) {
  const storage = useLocalStorage({ key: storageKey });

  const resolvedColumns = useMemo(() => {
    return Array.isArray(columns) ? columns : Object.values(columns);
  }, [columns]);

  const availableKeys = useMemo(() => {
    return resolvedColumns.map((col) => col.key);
  }, [resolvedColumns]);

  const defaultSelected = useMemo(() => {
    return resolvedColumns.filter((col) => col.default).map((col) => col.key);
  }, [resolvedColumns]);

  const [selected, setSelected] = useState(() => {
    try {
      if (!storage.enabled || !storage.hasValue()) return defaultSelected;

      const raw = storage.get();
      const parsed = raw ? JSON.parse(raw) : defaultSelected;

      const filtered = parsed.filter((key) => availableKeys.includes(key));
      const isDifferent = filtered.length !== parsed.length;

      if (isDifferent) {
        storage.set(JSON.stringify(filtered)); // persist corrected version
      }

      return filtered;
    } catch {
      return defaultSelected;
    }
  });

  const save = useCallback(
    (keys) => {
      try {
        const serialized = JSON.stringify(keys);
        storage.set(serialized);
        setSelected(keys);
      } catch {
        // silently fail
      }
    },
    [storage],
  );

  const visible = useMemo(() => {
    return Object.fromEntries(availableKeys.map((key) => [key, selected.includes(key)]));
  }, [availableKeys, selected]);

  return {
    visible,
    selected,
    save,
    all: columns,
  };
}
